import { z } from "zod";
import { MAX_UPLOAD_FILE_BYTES } from "@racler/shared";
import { event, request } from "./defs.ts";
import { projectIdSchema, relativePathSchema } from "./schemas.ts";

export const COLLISION_POLICIES = ["error", "overwrite", "rename"] as const;
export type CollisionPolicy = (typeof COLLISION_POLICIES)[number];
export const collisionPolicySchema = z.enum(COLLISION_POLICIES);

export const transferTargetKindSchema = z.enum(["project_file", "prompt_attachment"]);
export type TransferTargetKind = z.infer<typeof transferTargetKindSchema>;

export const transferStartRequest = request(
  "transfer",
  "transfer.start",
  z.strictObject({
    transferId: z.uuid(),
    projectId: projectIdSchema,
    targetKind: transferTargetKindSchema.optional(),
    destinationDir: relativePathSchema.optional(),
    fileName: z.string().min(1).max(255),
    size: z.number().int().min(0).max(MAX_UPLOAD_FILE_BYTES),
    mimeType: z.string().max(255).optional(),
    collisionPolicy: collisionPolicySchema.optional(),
  }),
  z.strictObject({
    transferId: z.uuid(),
    ready: z.literal(true),
  }),
);

export const transferCompleteRequest = request(
  "transfer",
  "transfer.complete",
  z.strictObject({
    transferId: z.uuid(),
    expectedSize: z.number().int().min(0).max(MAX_UPLOAD_FILE_BYTES),
    sha256: z.string().regex(/^[0-9a-f]{64}$/i).optional(),
  }),
  z.strictObject({
    transferId: z.uuid(),
    path: z.string(),
    name: z.string(),
    size: z.number().int().min(0),
    attachmentId: z.string().optional(),
    kind: z.enum(["text", "image", "pdf"]).optional(),
  }),
);

export const importAttachmentRequest = request(
  "transfer",
  "attachment.import",
  z.strictObject({
    projectId: projectIdSchema,
    attachmentId: z.uuid(),
    destinationDir: relativePathSchema.default(""),
    fileName: z.string().min(1).max(255).optional(),
    collisionPolicy: collisionPolicySchema.optional(),
  }),
  z.strictObject({
    path: z.string(),
    name: z.string(),
    size: z.number().int().min(0),
  }),
);

export const deleteAttachmentRequest = request(
  "transfer",
  "attachment.delete",
  z.strictObject({
    projectId: projectIdSchema,
    attachmentId: z.uuid(),
  }),
  z.strictObject({
    attachmentId: z.uuid(),
    deleted: z.literal(true),
  }),
);

export const transferCancelRequest = request(
  "transfer",
  "transfer.cancel",
  z.strictObject({
    transferId: z.uuid(),
    reason: z.string().max(255).optional(),
  }),
  z.strictObject({
    transferId: z.uuid(),
    cancelled: z.literal(true),
  }),
);

export const transferProgressEvent = event(
  "transfer",
  "transfer.progress",
  z.strictObject({
    transferId: z.uuid(),
    bytesReceived: z.number().int().min(0),
    totalBytes: z.number().int().min(0),
  }),
);
